import React, { useState, useCallback, useContext } from "react";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import {
	Button,
	Card,
	CardContent,
	CardActions,
	Grid,
	IconButton,
	Tooltip,
	Typography,
	CircularProgress,
	Chip,
} from "@material-ui/core";
import {
	Edit,
	CheckCircle,
	SignalCellularConnectedNoInternet2Bar,
	SignalCellularConnectedNoInternet0Bar,
	SignalCellular4Bar,
	CropFree,
	DeleteOutline,
	WhatsApp,
	Replay,
	PowerSettingsNew,
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import api from "../../services/api";
import WhatsAppModal from "../../components/WhatsAppModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import QrcodeModal from "../../components/QrcodeModal";
import { i18n } from "../../translate/i18n";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";
import EmptyState from "../../components/EmptyState";

const STATUS_CONFIG = {
	CONNECTED:    { label: "Conectado",       color: green[500], Icon: SignalCellular4Bar },
	DISCONNECTED: { label: "Desconectado",    color: "#f44336",  Icon: SignalCellularConnectedNoInternet0Bar },
	qrcode:       { label: "Aguard. QR Code", color: "#fb8c00",  Icon: CropFree },
	OPENING:      { label: "Conectando",      color: "#9e9e9e",  Icon: null },
	PENDING:      { label: "Reconectando",    color: "#fb8c00",  Icon: null },
	TIMEOUT:      { label: "Tempo esgotado",  color: "#ff9800",  Icon: SignalCellularConnectedNoInternet2Bar },
	PAIRING:      { label: "Sincronizando",   color: "#ff9800",  Icon: SignalCellularConnectedNoInternet2Bar },
};

const useStyles = makeStyles(theme => ({
	"@keyframes cardGlow": {
		"0%":   { boxShadow: "0 0 0 0 rgba(251,140,0,0.45)" },
		"70%":  { boxShadow: "0 0 0 10px rgba(251,140,0,0)" },
		"100%": { boxShadow: "0 0 0 0 rgba(251,140,0,0)" },
	},
	"@keyframes iconPulse": {
		"0%":   { transform: "scale(1)",    opacity: 1   },
		"50%":  { transform: "scale(1.18)", opacity: 0.6 },
		"100%": { transform: "scale(1)",    opacity: 1   },
	},
	"@keyframes dotBlink": {
		"0%, 80%, 100%": { opacity: 0 },
		"40%":           { opacity: 1 },
	},
	gridWrapper: {
		padding: theme.spacing(2),
	},
	scrollContainer: {
		flex: 1,
		overflowX: "hidden",
		overflowY: "auto",
		...theme.scrollbarStyles,
	},
	card: {
		display: "flex",
		flexDirection: "column",
		height: "100%",
		borderRadius: theme.spacing(1),
		transition: "box-shadow 0.2s",
		"&:hover": {
			boxShadow: theme.shadows[6],
		},
	},
	cardProcessing: {
		animation: "$cardGlow 1.8s ease-in-out infinite",
		border: "1px solid rgba(251,140,0,0.4)",
	},
	cardContent: {
		flex: 1,
	},
	cardHeader: {
		display: "flex",
		alignItems: "flex-start",
		gap: theme.spacing(1.5),
		marginBottom: theme.spacing(1),
	},
	whatsappIcon: {
		fontSize: 44,
		color: "#25D366",
		flexShrink: 0,
		marginTop: 2,
	},
	connectionName: {
		fontWeight: 600,
		lineHeight: 1.3,
		wordBreak: "break-word",
		color: theme.palette.text.primary,
	},
	statusRow: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(0.75),
		marginTop: theme.spacing(0.5),
		flexWrap: "wrap",
	},
	statusText: {
		display: "flex",
		alignItems: "center",
		gap: 4,
		fontSize: "0.78rem",
		fontWeight: 600,
	},
	qrIcon: {
		animation: "$iconPulse 1.8s ease-in-out infinite",
	},
	dots: {
		display: "inline-flex",
		gap: 2,
		marginLeft: 2,
		"& span": {
			width: 4,
			height: 4,
			borderRadius: "50%",
			backgroundColor: "currentColor",
			animation: "$dotBlink 1.4s ease-in-out infinite",
			"&:nth-child(2)": { animationDelay: "0.2s" },
			"&:nth-child(3)": { animationDelay: "0.4s" },
		},
	},
	defaultChip: {
		height: 20,
		fontSize: "0.68rem",
		color: green[700],
		borderColor: green[300],
		"& .MuiChip-icon": {
			fontSize: 14,
			color: green[500],
		},
	},
	updateInfo: {
		display: "block",
		marginTop: theme.spacing(1.5),
		color: theme.palette.text.secondary,
		fontSize: "0.75rem",
	},
	cardActions: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		gap: theme.spacing(1),
		padding: theme.spacing(1, 1.5),
		borderTop: `1px solid ${theme.palette.divider}`,
		minHeight: 52,
	},
	actionButtons: {
		display: "flex",
		flexWrap: "wrap",
		gap: theme.spacing(0.75),
	},
	actionBtn: {
		borderRadius: 8,
		textTransform: "none",
		fontWeight: 600,
		fontSize: "0.78rem",
		boxShadow: "none",
		whiteSpace: "nowrap",
		"& .MuiButton-startIcon": {
			marginRight: 4,
		},
	},
	disconnectBtn: {
		color: "#f44336",
		borderColor: "rgba(244,67,54,0.5)",
		"&:hover": {
			borderColor: "#f44336",
			backgroundColor: "rgba(244,67,54,0.08)",
		},
	},
	rowActions: {
		display: "flex",
		alignItems: "center",
		flexShrink: 0,
	},
	loadingWrapper: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		minHeight: 200,
	},
}));

const Connections = () => {
	const classes = useStyles();

	const { user } = useContext(AuthContext);
	const { whatsApps, loading } = useContext(WhatsAppsContext);
	const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
	const [qrModalOpen, setQrModalOpen] = useState(false);
	const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const confirmationModalInitialState = {
		action: "",
		title: "",
		message: "",
		whatsAppId: "",
		open: false,
	};
	const [confirmModalInfo, setConfirmModalInfo] = useState(
		confirmationModalInitialState
	);

	const handleStartWhatsAppSession = async whatsAppId => {
		try {
			await api.post(`/whatsappsession/${whatsAppId}`);
		} catch (err) {
			toastError(err);
		}
	};

	const handleRequestNewQrCode = async whatsAppId => {
		try {
			await api.put(`/whatsappsession/${whatsAppId}`);
		} catch (err) {
			toastError(err);
		}
	};

	const handleOpenWhatsAppModal = () => {
		setSelectedWhatsApp(null);
		setWhatsAppModalOpen(true);
	};

	const handleCloseWhatsAppModal = useCallback(() => {
		setWhatsAppModalOpen(false);
		setSelectedWhatsApp(null);
	}, [setSelectedWhatsApp, setWhatsAppModalOpen]);

	const handleOpenQrModal = whatsApp => {
		setSelectedWhatsApp(whatsApp);
		setQrModalOpen(true);
	};

	const handleCloseQrModal = useCallback(() => {
		setSelectedWhatsApp(null);
		setQrModalOpen(false);
	}, [setQrModalOpen, setSelectedWhatsApp]);

	const handleEditWhatsApp = whatsApp => {
		setSelectedWhatsApp(whatsApp);
		setWhatsAppModalOpen(true);
	};

	const handleOpenConfirmationModal = (action, whatsAppId) => {
		if (action === "disconnect") {
			setConfirmModalInfo({
				action,
				title: i18n.t("connections.confirmationModal.disconnectTitle"),
				message: i18n.t("connections.confirmationModal.disconnectMessage"),
				whatsAppId,
			});
		}
		if (action === "delete") {
			setConfirmModalInfo({
				action,
				title: i18n.t("connections.confirmationModal.deleteTitle"),
				message: i18n.t("connections.confirmationModal.deleteMessage"),
				whatsAppId,
			});
		}
		setConfirmModalOpen(true);
	};

	const handleSubmitConfirmationModal = async () => {
		if (confirmModalInfo.action === "disconnect") {
			try {
				await api.delete(`/whatsappsession/${confirmModalInfo.whatsAppId}`);
			} catch (err) {
				toastError(err);
			}
		}
		if (confirmModalInfo.action === "delete") {
			try {
				await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`);
				toast.success(i18n.t("connections.toasts.deleted"));
			} catch (err) {
				toastError(err);
			}
		}
		setConfirmModalInfo(confirmationModalInitialState);
	};

	const renderStatusBadge = whatsApp => {
		const config = STATUS_CONFIG[whatsApp.status];
		if (!config) return null;

		if (whatsApp.status === "OPENING" || whatsApp.status === "PENDING") {
			return (
				<div className={classes.statusText} style={{ color: config.color }}>
					<CircularProgress size={12} style={{ color: config.color }} />
					<span>{config.label}</span>
					<span className={classes.dots}>
						<span /><span /><span />
					</span>
				</div>
			);
		}

		if (whatsApp.status === "qrcode") {
			return (
				<div className={classes.statusText} style={{ color: config.color }}>
					<CropFree className={classes.qrIcon} style={{ fontSize: 15, color: config.color }} />
					<span>Aguardando leitura</span>
					<span className={classes.dots}>
						<span /><span /><span />
					</span>
				</div>
			);
		}

		const { Icon, label, color } = config;
		return (
			<div className={classes.statusText} style={{ color }}>
				{Icon && <Icon style={{ fontSize: 15, color }} />}
				<span>{label}</span>
			</div>
		);
	};

	const renderActionButtons = whatsApp => {
		const { status, id } = whatsApp;

		const tryAgainBtn = (
			<Button
				className={classes.actionBtn}
				size="small"
				variant="contained"
				color="primary"
				startIcon={<Replay />}
				onClick={() => handleStartWhatsAppSession(id)}
			>
				{i18n.t("connections.buttons.tryAgain")}
			</Button>
		);
		const newQrBtn = (
			<Button
				className={classes.actionBtn}
				size="small"
				variant="outlined"
				color="primary"
				startIcon={<CropFree />}
				onClick={() => handleRequestNewQrCode(id)}
			>
				{i18n.t("connections.buttons.newQr")}
			</Button>
		);
		const disconnectBtn = (
			<Button
				className={`${classes.actionBtn} ${classes.disconnectBtn}`}
				size="small"
				variant="outlined"
				startIcon={<PowerSettingsNew />}
				onClick={() => handleOpenConfirmationModal("disconnect", id)}
			>
				{i18n.t("connections.buttons.disconnect")}
			</Button>
		);

		return (
			<div className={classes.actionButtons}>
				{status === "qrcode" && (
					<Button
						className={classes.actionBtn}
						size="small"
						variant="contained"
						color="primary"
						startIcon={<CropFree />}
						onClick={() => handleOpenQrModal(whatsApp)}
					>
						{i18n.t("connections.buttons.qrcode")}
					</Button>
				)}

				{(status === "DISCONNECTED" || status === "TIMEOUT") && (
					<>
						{tryAgainBtn}
						{newQrBtn}
					</>
				)}

				{/* PENDING: sessão caiu/aguardando re-vínculo — permite reconectar
				    ou encerrar de vez (foi o caso do botão de desconectar sumido). */}
				{status === "PENDING" && (
					<>
						{tryAgainBtn}
						{disconnectBtn}
					</>
				)}

				{(status === "CONNECTED" || status === "PAIRING") && disconnectBtn}

				{status === "OPENING" && (
					<Button
						className={classes.actionBtn}
						size="small"
						variant="outlined"
						disabled
						startIcon={<CircularProgress size={13} />}
					>
						{i18n.t("connections.buttons.connecting")}
					</Button>
				)}
			</div>
		);
	};

	return (
		<MainContainer>
			<ConfirmationModal
				title={confirmModalInfo.title}
				open={confirmModalOpen}
				onClose={setConfirmModalOpen}
				onConfirm={handleSubmitConfirmationModal}
			>
				{confirmModalInfo.message}
			</ConfirmationModal>
			<QrcodeModal
				open={qrModalOpen}
				onClose={handleCloseQrModal}
				whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id}
			/>
			<WhatsAppModal
				open={whatsAppModalOpen}
				onClose={handleCloseWhatsAppModal}
				whatsAppId={!qrModalOpen && selectedWhatsApp?.id}
			/>
			<MainHeader>
				<Title>{i18n.t("connections.title")}</Title>
				<MainHeaderButtonsWrapper>
					<Can
						role={user.profile}
						perform="connections-page:addConnection"
						yes={() => (
							<Button
								variant="contained"
								color="primary"
								onClick={handleOpenWhatsAppModal}
							>
								{i18n.t("connections.buttons.add")}
							</Button>
						)}
					/>
				</MainHeaderButtonsWrapper>
			</MainHeader>

			{loading ? (
				<div className={classes.loadingWrapper}>
					<CircularProgress />
				</div>
			) : !whatsApps || whatsApps.length === 0 ? (
				<EmptyState
					icon="📱"
					title="Nenhuma conexão WhatsApp ainda"
					description="Conecte seu WhatsApp para começar a receber e enviar mensagens. O processo leva menos de 2 minutos."
					actionLabel="+ Nova Conexão"
					onAction={handleOpenWhatsAppModal}
				/>
			) : (
				<div className={classes.scrollContainer}>
				<Grid container spacing={2} className={classes.gridWrapper}>
					{whatsApps?.map(whatsApp => (
						<Grid item xs={12} sm={6} md={4} key={whatsApp.id}>
							<Card
								className={`${classes.card} ${
									whatsApp.status === "qrcode" || whatsApp.status === "OPENING"
										? classes.cardProcessing
										: ""
								}`}
								elevation={2}
							>
								<CardContent className={classes.cardContent}>
									<div className={classes.cardHeader}>
										<WhatsApp className={classes.whatsappIcon} />
										<div style={{ flex: 1, minWidth: 0 }}>
											<Typography
												variant="subtitle1"
												className={classes.connectionName}
											>
												{whatsApp.name}
											</Typography>
											<div className={classes.statusRow}>
												{renderStatusBadge(whatsApp)}
												{whatsApp.isDefault && (
													<Chip
														label="Padrão"
														size="small"
														icon={<CheckCircle />}
														variant="outlined"
														className={classes.defaultChip}
													/>
												)}
											</div>
										</div>
									</div>
									<Typography className={classes.updateInfo}>
										Atualizado:{" "}
										{format(parseISO(whatsApp.updatedAt), "dd/MM/yy HH:mm")}
									</Typography>
								</CardContent>

								<CardActions className={classes.cardActions}>
									<Can
										role={user.profile}
										perform="connections-page:actionButtons"
										yes={() => renderActionButtons(whatsApp)}
									/>
									<Can
										role={user.profile}
										perform="connections-page:editOrDeleteConnection"
										yes={() => (
											<div className={classes.rowActions}>
												<Tooltip title="Editar">
													<IconButton
														size="small"
														onClick={() => handleEditWhatsApp(whatsApp)}
													>
														<Edit fontSize="small" />
													</IconButton>
												</Tooltip>
												<Tooltip title="Excluir">
													<IconButton
														size="small"
														onClick={() =>
															handleOpenConfirmationModal("delete", whatsApp.id)
														}
													>
														<DeleteOutline fontSize="small" />
													</IconButton>
												</Tooltip>
											</div>
										)}
									/>
								</CardActions>
							</Card>
						</Grid>
					))}
				</Grid>
				</div>
			)}
		</MainContainer>
	);
};

export default Connections;
